/**
 * QuestionField Component
 *
 * Renders a single question field in the form renderer.
 * Supports text, rating, select, checkbox, radio, and media (audio/video/image) types.
 */

import React, { useState } from 'react'
import MediaCapture from './MediaCapture'
import {
  COUNTRY_CODES,
  getDefaultDialCode,
  validatePhoneNumber,
} from '../../constants/countryCodes'
import { getRatingPreset } from '../../constants/ratingPresets'

/**
 * OpenResponseField — lets the respondent choose text, audio, or image mode.
 */
function OpenResponseField({
  question,
  value,
  onChange,
  surveyId,
  responseId,
  uploadToken,
  allowedResponseTypes,
  error,
}) {
  const openValue =
    value && typeof value === 'object' ? value : { mode: 'text', text: '', media_url: null }
  const [mode, setMode] = useState(openValue.mode || 'text')

  const handleModeChange = (newMode) => {
    setMode(newMode)
    // Preserve any existing data but update the mode
    onChange({ ...openValue, mode: newMode })
  }

  const handleTextChange = (e) => {
    onChange({ ...openValue, mode: 'text', text: e.target.value })
  }

  const handleMediaChange = (mediaValue) => {
    onChange({ ...openValue, mode, ...mediaValue })
  }

  // Check which media modes are enabled in the survey
  const audioEnabled = allowedResponseTypes.includes('audio')
  const imageEnabled = allowedResponseTypes.includes('image')

  return (
    <div className={`open-response-field ${error ? 'has-error' : ''}`}>
      <div className="open-response-tabs">
        <button
          type="button"
          className={`or-tab ${mode === 'text' ? 'active' : ''}`}
          onClick={() => handleModeChange('text')}>
          Text
        </button>
        <button
          type="button"
          className={`or-tab ${mode === 'audio' ? 'active' : ''} ${!audioEnabled ? 'disabled' : ''}`}
          onClick={() => audioEnabled && handleModeChange('audio')}
          disabled={!audioEnabled}
          title={!audioEnabled ? 'Audio not enabled for this survey' : ''}>
          Audio
        </button>
        <button
          type="button"
          className={`or-tab ${mode === 'image' ? 'active' : ''} ${!imageEnabled ? 'disabled' : ''}`}
          onClick={() => imageEnabled && handleModeChange('image')}
          disabled={!imageEnabled}
          title={!imageEnabled ? 'Image not enabled for this survey' : ''}>
          Image
        </button>
      </div>

      <div className="open-response-content">
        {mode === 'text' && (
          <textarea
            className={`field-input field-textarea ${error ? 'has-error' : ''}`}
            value={openValue.text || ''}
            onChange={handleTextChange}
            placeholder="Type your response..."
            rows={4}
          />
        )}
        {mode === 'audio' && audioEnabled && (
          <MediaCapture
            type="audio"
            questionId={question.id}
            surveyId={surveyId}
            responseId={responseId}
            uploadToken={uploadToken}
            value={openValue}
            onChange={handleMediaChange}
            error={error}
            maxDurationMs={300000}
          />
        )}
        {mode === 'image' && imageEnabled && (
          <MediaCapture
            type="image"
            questionId={question.id}
            surveyId={surveyId}
            responseId={responseId}
            uploadToken={uploadToken}
            value={openValue}
            onChange={handleMediaChange}
            error={error}
            allowUpload={true}
          />
        )}
        {mode === 'audio' && !audioEnabled && (
          <p className="field-disabled">Audio responses are not enabled for this survey.</p>
        )}
        {mode === 'image' && !imageEnabled && (
          <p className="field-disabled">Image responses are not enabled for this survey.</p>
        )}
      </div>
    </div>
  )
}

export default function QuestionField({
  question,
  index,
  questionNumber,
  value,
  error,
  onChange,
  onBlur,
  surveyId,
  responseId,
  uploadToken,
  allowedResponseTypes = ['text'],
  backgroundColor,
}) {
  const handleChange = (e) => {
    const { type, checked } = e.target
    let newValue = e.target.value

    // Handle checkbox multi-select
    if (type === 'checkbox' && question.type === 'checkbox') {
      const optionValue = e.target.value
      const currentValues = Array.isArray(value) ? value : []
      if (checked) {
        newValue = [...currentValues, optionValue]
      } else {
        newValue = currentValues.filter((v) => v !== optionValue)
      }
    }

    onChange(newValue)
  }

  const handleRatingClick = (rating) => {
    onChange(rating)
  }

  const renderInput = () => {
    switch (question.type) {
      case 'text':
        return (
          <input
            type="text"
            id={`input-${question.id}`}
            value={value || ''}
            onChange={handleChange}
            placeholder={question.placeholder || ''}
            className={`field-input ${error ? 'has-error' : ''}`}
          />
        )

      case 'textarea':
        return (
          <textarea
            id={`input-${question.id}`}
            value={value || ''}
            onChange={handleChange}
            placeholder={question.placeholder || ''}
            rows={question.rows || 4}
            className={`field-input field-textarea ${error ? 'has-error' : ''}`}
          />
        )

      case 'email':
        return (
          <input
            type="email"
            id={`input-${question.id}`}
            value={value || ''}
            onChange={handleChange}
            placeholder={question.placeholder || 'email@example.com'}
            className={`field-input ${error ? 'has-error' : ''}`}
          />
        )

      case 'number':
        return (
          <input
            type="number"
            id={`input-${question.id}`}
            value={value ?? ''}
            onChange={handleChange}
            placeholder={question.placeholder || ''}
            min={question.min}
            max={question.max}
            className={`field-input ${error ? 'has-error' : ''}`}
          />
        )

      case 'phone': {
        // Auto-detect the RESPONDER's country from their timezone, not the creator's
        const detectedDialCode = getDefaultDialCode()
        const phoneValue =
          value && typeof value === 'object' ? value : { countryCode: detectedDialCode, number: '' }
        return (
          <div
            className={`field-phone ${error ? 'has-error' : ''}`}
            style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <select
              value={phoneValue.countryCode}
              onChange={(e) => {
                onChange({ ...phoneValue, countryCode: e.target.value })
                // Re-validate when country code changes (may invalidate existing number)
                setTimeout(() => onBlur?.(question.id), 0)
              }}
              className="field-input"
              style={{ width: 'auto', minWidth: '130px', flex: '0 0 auto' }}>
              {COUNTRY_CODES.map((c) => (
                <option key={`${c.code}-${c.dialCode}`} value={c.dialCode}>
                  {c.code} {c.dialCode}
                </option>
              ))}
            </select>
            <input
              type="tel"
              id={`input-${question.id}`}
              value={phoneValue.number || ''}
              onChange={(e) => {
                // Allow only digits, spaces, and dashes
                const cleaned = e.target.value.replace(/[^\d\s-]/g, '')
                onChange({ ...phoneValue, number: cleaned })
              }}
              onBlur={(e) => {
                onBlur?.(question.id)
                // Refocus the input if number is present but invalid,
                // so the respondent fixes it before moving on
                if (phoneValue.number?.trim()) {
                  const result = validatePhoneNumber(phoneValue.countryCode, phoneValue.number)
                  if (!result.valid) {
                    // Small delay so the error message renders first
                    setTimeout(() => e.target.focus(), 0)
                  }
                }
              }}
              placeholder={question.placeholder || 'Phone number'}
              className={`field-input ${error ? 'has-error' : ''}`}
              style={{ flex: 1 }}
              inputMode="tel"
            />
          </div>
        )
      }

      case 'rating': {
        const maxRating = question.maxRating || 5
        const currentRating = value || 0
        const ratingStyle = question.ratingStyle || 'stars'
        const isCustom = ratingStyle === 'custom'
        const preset = !isCustom ? getRatingPreset(ratingStyle) : null
        const customIcons = question.customRatingIcons || []

        return (
          <div className={`field-rating ${error ? 'has-error' : ''}`}>
            {Array.from({ length: maxRating }, (_, i) => i + 1).map((rating) => {
              const isSelected = rating <= currentRating
              const idx = rating - 1

              // Custom uploaded images
              if (isCustom && customIcons[idx]) {
                return (
                  <button
                    key={rating}
                    type="button"
                    className={`rating-icon rating-custom ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleRatingClick(rating)}
                    aria-label={`Rate ${rating} out of ${maxRating}`}>
                    <img src={customIcons[idx]} alt={`${rating}`} className="rating-custom-img" />
                  </button>
                )
              }

              // Preset emoji/icon
              if (preset) {
                const icon = preset.getIcon(idx, maxRating, isSelected)
                const color = isSelected ? preset.getColor(idx, maxRating) : preset.unselectedColor
                return (
                  <button
                    key={rating}
                    type="button"
                    className={`rating-icon ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleRatingClick(rating)}
                    aria-label={`Rate ${rating} out of ${maxRating}`}
                    style={{ color }}>
                    {icon}
                  </button>
                )
              }

              // Fallback stars
              return (
                <button
                  key={rating}
                  type="button"
                  className={`rating-star ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleRatingClick(rating)}
                  aria-label={`Rate ${rating} out of ${maxRating}`}>
                  {isSelected ? '★' : '☆'}
                </button>
              )
            })}
            {currentRating > 0 && (
              <span className="rating-value">
                {currentRating} / {maxRating}
              </span>
            )}
          </div>
        )
      }

      case 'select':
        return (
          <select
            id={`input-${question.id}`}
            value={value || ''}
            onChange={handleChange}
            className={`field-input field-select ${error ? 'has-error' : ''}`}>
            <option value="">Select an option...</option>
            {(question.options || []).map((opt, i) => (
              <option key={i} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        )

      case 'radio':
        return (
          <div className={`field-options ${error ? 'has-error' : ''}`}>
            {(question.options || []).map((opt, i) => (
              <label key={i} className="field-option">
                <input
                  type="radio"
                  name={`radio-${question.id}`}
                  value={opt}
                  checked={value === opt}
                  onChange={handleChange}
                />
                <span className="option-label">{opt}</span>
              </label>
            ))}
          </div>
        )

      case 'checkbox': {
        const selectedValues = Array.isArray(value) ? value : []
        return (
          <div className={`field-options ${error ? 'has-error' : ''}`}>
            {(question.options || []).map((opt, i) => (
              <label key={i} className="field-option">
                <input
                  type="checkbox"
                  name={`checkbox-${question.id}`}
                  value={opt}
                  checked={selectedValues.includes(opt)}
                  onChange={handleChange}
                />
                <span className="option-label">{opt}</span>
              </label>
            ))}
          </div>
        )
      }

      case 'open_response': {
        return (
          <OpenResponseField
            question={question}
            value={value}
            onChange={onChange}
            surveyId={surveyId}
            responseId={responseId}
            uploadToken={uploadToken}
            allowedResponseTypes={allowedResponseTypes}
            error={error}
          />
        )
      }

      case 'audio': {
        // Check if audio responses are allowed
        if (!allowedResponseTypes.includes('audio')) {
          return <p className="field-disabled">Audio responses are not enabled for this survey.</p>
        }
        return (
          <MediaCapture
            type="audio"
            questionId={question.id}
            surveyId={surveyId}
            responseId={responseId}
            uploadToken={uploadToken}
            value={value}
            onChange={onChange}
            error={error}
            maxDurationMs={question.maxDurationMs || 300000}
          />
        )
      }

      case 'video': {
        // Check if video responses are allowed
        if (!allowedResponseTypes.includes('video')) {
          return <p className="field-disabled">Video responses are not enabled for this survey.</p>
        }
        return (
          <MediaCapture
            type="video"
            questionId={question.id}
            surveyId={surveyId}
            responseId={responseId}
            uploadToken={uploadToken}
            value={value}
            onChange={onChange}
            error={error}
            maxDurationMs={question.maxDurationMs || 120000}
          />
        )
      }

      case 'image': {
        // Check if image responses are allowed
        if (!allowedResponseTypes.includes('image')) {
          return <p className="field-disabled">Image responses are not enabled for this survey.</p>
        }
        return (
          <MediaCapture
            type="image"
            questionId={question.id}
            surveyId={surveyId}
            responseId={responseId}
            uploadToken={uploadToken}
            value={value}
            onChange={onChange}
            error={error}
            allowUpload={question.allowUpload !== false}
          />
        )
      }

      default:
        return <p className="field-unknown">Unknown question type: {question.type}</p>
    }
  }

  // Section header — visual only, no input
  if (question.type === 'section_header') {
    return (
      <div id={`question-${question.id}`} className="section-header-field">
        <h3 className="section-header-title">{question.label || 'Section'}</h3>
        {question.description && <p className="section-header-desc">{question.description}</p>}
      </div>
    )
  }

  // Divider — visual only
  if (question.type === 'divider') {
    return (
      <div id={`question-${question.id}`} className="section-divider-field">
        <hr className="section-divider-line" />
      </div>
    )
  }

  const bgStyle = backgroundColor ? { backgroundColor } : {}

  return (
    <div
      id={`question-${question.id}`}
      className={`question-field ${error ? 'has-error' : ''}`}
      style={bgStyle}>
      <label htmlFor={`input-${question.id}`} className="field-label">
        <span className="question-number">{questionNumber ?? index + 1}.</span>
        {question.label || 'Question'}
        {question.required && <span className="required-indicator">*</span>}
      </label>
      {renderInput()}
      {error && <div className="field-error">{error}</div>}
    </div>
  )
}
